"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { Search, X, Loader2, CornerDownLeft } from "lucide-react";

type Resultado = {
  tipo: string;
  titulo: string;
  sub: string;
  href: string;
  valor?: string;
};

export default function PesquisaGlobal() {
  const [open, setOpen] = useState(false);
  const [termo, setTermo] = useState("");
  const [res, setRes] = useState<Resultado[]>([]);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  // Atalho Ctrl/Cmd+K e Esc
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 40);
    } else {
      setTermo("");
      setRes([]);
      setSel(0);
    }
  }, [open]);

  // Pesquisa (debounced)
  useEffect(() => {
    if (termo.trim().length < 2) {
      setRes([]);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`/api/pesquisa?q=${encodeURIComponent(termo)}`);
        const j = await r.json();
        setRes(j.resultados || []);
        setSel(0);
      } catch {
        setRes([]);
      }
      setLoading(false);
    }, 200);
    return () => clearTimeout(t);
  }, [termo]);

  function ir(href: string) {
    setOpen(false);
    router.push(href);
  }

  function navKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSel((s) => Math.min(s + 1, res.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSel((s) => Math.max(s - 1, 0));
    } else if (e.key === "Enter" && res[sel]) {
      ir(res[sel].href);
    }
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex w-full items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-400 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:bg-neutral-800"
      >
        <Search size={15} />
        <span className="flex-1 text-left">Pesquisar…</span>
        <kbd className="rounded border border-neutral-200 px-1.5 py-0.5 text-[10px] text-neutral-400 dark:border-neutral-700">
          Ctrl K
        </kbd>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-[12vh]"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-xl overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-2xl dark:border-neutral-800 dark:bg-neutral-900"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 border-b border-neutral-100 px-4 py-3 dark:border-neutral-800">
              <Search size={17} className="text-neutral-400" />
              <input
                ref={inputRef}
                value={termo}
                onChange={(e) => setTermo(e.target.value)}
                onKeyDown={navKey}
                placeholder="Fornecedor, fatura, movimento, artigo, colaborador…"
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-neutral-400"
              />
              {loading && <Loader2 size={15} className="animate-spin text-neutral-400" />}
              <button
                onClick={() => setOpen(false)}
                className="text-neutral-400 hover:text-neutral-600"
                aria-label="Fechar"
              >
                <X size={16} />
              </button>
            </div>

            <div className="max-h-[52vh] overflow-y-auto p-2">
              {termo.trim().length < 2 && (
                <p className="px-3 py-6 text-center text-sm text-neutral-400">
                  Escreve pelo menos 2 letras para pesquisar.
                </p>
              )}
              {termo.trim().length >= 2 && res.length === 0 && !loading && (
                <p className="px-3 py-6 text-center text-sm text-neutral-400">
                  Sem resultados para &quot;{termo}&quot;.
                </p>
              )}
              {res.map((r, i) => (
                <button
                  key={i}
                  onClick={() => ir(r.href)}
                  onMouseEnter={() => setSel(i)}
                  className={`flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left ${
                    i === sel
                      ? "bg-neutral-100 dark:bg-neutral-800"
                      : "hover:bg-neutral-50 dark:hover:bg-neutral-800/50"
                  }`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="shrink-0 rounded bg-teal-50 px-1.5 py-0.5 text-[10px] font-medium uppercase text-teal-700 dark:bg-teal-950 dark:text-teal-300">
                        {r.tipo}
                      </span>
                      <span className="truncate text-sm font-medium">{r.titulo}</span>
                    </div>
                    {r.sub && (
                      <div className="mt-0.5 truncate text-xs text-neutral-400">
                        {r.sub}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {r.valor && (
                      <span className="text-sm tabular-nums text-neutral-500">
                        {r.valor}
                      </span>
                    )}
                    {i === sel && (
                      <CornerDownLeft size={14} className="text-neutral-400" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
